import React, { useRef, useContext, useMemo } from 'react'
import './RelatedProducts.css'
import { ShopContext } from '../../Context/ShopContext'
import Item from '../Item/Item'

const RelatedProducts = ({ product }) => {

    const { all_products } = useContext(ShopContext)
    const carouselRef = useRef(null);

    // ── Relevance scoring ─────────────────────────────────────────────────
    // 2 pts per shared tag, 1 pt for same category
    // Must score > 0 and not be the current product
    const relatedProducts = useMemo(() => {
        if (!product || !all_products.length) return []
 
        const currentTags = product.tags || []
        const currentCategory = product.category || ""
 
        return all_products
            .filter(p => p.id !== product.id)
            .map(p => {
                let score = 0
                // Tag matches — 2 pts each
                const sharedTags = (p.tags || []).filter(tag => currentTags.includes(tag))
                score += sharedTags.length * 2
                // Category match — 1 pt
                if (p.category === currentCategory) score += 1
                return { ...p, _score: score }
            })
            .filter(p => p._score > 0)
            .sort((a, b) => b._score - a._score)
            .slice(0, 8)
    }, [product, all_products])

     // Fallback: if fewer than 3 related found, fill with same-category products
    const displayProducts = useMemo(() => {
        if (relatedProducts.length >= 3) return relatedProducts
 
        const relatedIds = new Set(relatedProducts.map(p => p.id))
        const fallback   = all_products
            .filter(p => p.id !== product?.id && !relatedIds.has(p.id) && p.category === product?.category)
            .slice(0, 8 - relatedProducts.length)
 
        return [...relatedProducts, ...fallback]
    }, [relatedProducts, all_products, product])

    const scrollLeft = () => {
        carouselRef.current?.scrollBy({ left: -320, behavior: 'smooth' })
    }
 
    const scrollRight = () => {
        carouselRef.current?.scrollBy({ left: 320, behavior: 'smooth' })
    }
 
    if (!displayProducts.length) return null

    // ── Relevance label ───────────────────────────────────────────────────
    // Shows what the section is based on
    const sectionLabel = (() => {
        const currentTags = product?.tags || []
        if (currentTags.length > 0) {
            // Find the most-matched tag across display products for the label
            const tagCounts = {}
            displayProducts.forEach(p => {
                (p.tags || []).forEach(tag => {
                    if (currentTags.includes(tag)) tagCounts[tag] = (tagCounts[tag] || 0) + 1
                })
            })
            const topTag = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])[0]
            if (topTag) return `More ${topTag[0]} Picks`
        }
        return 'You May Also Like'
    })()

    return (
        <div className='related-products'>
            <div className="related-products-header">
                <h1>{sectionLabel}</h1>
                <p className="related-products-subtitle" style={{ textTransform: 'capitalize' }}>
                    Based on {product?.category} · {(product?.tags || []).join(', ')}
                </p>
            </div>
 
            <div className="carousel-wrapper">
                <button className='carousel-btn left' onClick={scrollLeft} aria-label="Scroll left">&#10094;</button>
 
                <div className="related-products-items" ref={carouselRef}>
                    {displayProducts.map((item) => (
                        <Item
                            key={item.id}
                            id={item.id}
                            name={item.name}  
                            discount_percentage={item.discount_percentage} 
                            available={item.available}
                            images={item.images}
                            price={item.price}
                            old_price={item.old_price}
                        />
                    ))}
                </div>
 
                <button className='carousel-btn right' onClick={scrollRight} aria-label="Scroll right">&#10095;</button>
            </div>
        </div>
    )
}

export default RelatedProducts